// ----------------------------------------------------------------------------
// INLINE SETLIST BUILDER
// Shown in Roster when team's setlist mode is "admin_predefined"
// Admin can search songs from library + YouTube, add items, reorder, set keys, save as template
// ----------------------------------------------------------------------------

import { useState, useEffect, useRef } from 'react';
import { Music, Plus, Save, FolderOpen, Share2, ChevronDown, ChevronRight, LockKeyhole } from 'lucide-react';
import type { PlanItem, Song, SetlistTemplate } from '@app/types';
import type { YouTubeSearchResult } from '@app/services/youtube';
import { Button, Input, Menu, MenuItem, Select } from '@app/components/ui';

import type { PendingYouTubeSong, SetlistBuilderProps } from './setlist/types';
import { DEFAULT_SECTIONS, ITEM_TYPES } from './setlist/constants';
import { SetlistItem } from './setlist/SetlistItem';
import { SongSearch } from './setlist/SongSearch';
import { SetlistLimitModal } from './setlist/SetlistLimitModal';
import { SectionManager } from './setlist/SectionManager';
import { SaveTemplateModal } from './setlist/SaveTemplateModal';
import { useSetlistShare } from './setlist/useSetlistShare';
import { UpgradeFeatureModal } from '@app/components/UpgradeFeatureModal';
import { useSubscriptionFeatures } from '@admin/shared/hooks/useSubscriptionFeatures';

export type { PendingYouTubeSong } from './setlist/types';

export const SetlistBuilder: React.FC<SetlistBuilderProps> = ({
  churchId,
  plan,
  groupId,
  onItemsChange,
  onSaveTemplate,
  templates,
  onLoadTemplate,
  onPendingYouTubeSongs,
}) => {
  const { features } = useSubscriptionFeatures();
  const [items, setItems] = useState<PlanItem[]>(plan.items || []);
  const [collapsed, setCollapsed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const templateBtnRef = useRef<HTMLButtonElement>(null);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showTemplateUpgrade, setShowTemplateUpgrade] = useState(false);
  const [addingType, setAddingType] = useState<string>('Song');
  const [customTitle, setCustomTitle] = useState('');
  const pendingYouTubeRef = useRef<Map<string, PendingYouTubeSong>>(new Map());

  // Custom sections — derived from items + allow adding new ones
  const [customSections, setCustomSections] = useState<string[]>(() => {
    const fromItems = (plan.items || []).map(i => i.section).filter(Boolean) as string[];
    return [...new Set([...DEFAULT_SECTIONS, ...fromItems])];
  });

  // Share functionality
  const share = useSetlistShare(items, plan);

  // Track YouTube-sourced songs pending library save

  // Sync items when a *different* plan is selected (plan.id changes).
  // Do NOT depend on plan.items — its reference changes on every parent
  // re-render, which resets local drag state mid-drag causing flicker.
  useEffect(() => {
    setItems(plan.items || []);
    // Also sync sections from plan, always include defaults
    const fromItems = (plan.items || []).map(i => i.section).filter(Boolean) as string[];
    setCustomSections(prev => {
      const merged = new Set([...DEFAULT_SECTIONS, ...prev, ...fromItems]);
      return [...merged];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.id]);

  // Notify parent when items change
  const onItemsChangeRef = useRef(onItemsChange);
  onItemsChangeRef.current = onItemsChange;

  useEffect(() => {
    onItemsChangeRef.current(items);
  }, [items]);

  // --- Item CRUD ---

  const addSongItem = (song: Song) => {
    const newItem: PlanItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: 'Song',
      title: song.title,
      songId: song.id,
      key: song.defaultKey || '',
      notes: '',
      youtubeUrl: song.youtubeUrl || '',
      lyrics: song.lyrics || '',
    };
    setItems(prev => [...prev, newItem]);
    setShowSearch(false);
  };

  const addYouTubeItem = (result: YouTubeSearchResult) => {
    // Decode HTML entities in YouTube title
    const decodedTitle = result.snippet.title
      .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"').replace(/&#39;/g, "'");

    const itemId = `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const newItem: PlanItem = {
      id: itemId,
      type: 'Song',
      title: decodedTitle,
      key: '',
      notes: '',
      youtubeUrl: `https://www.youtube.com/watch?v=${result.id.videoId}`,
    };
    // Track as pending YouTube song for library save on publish
    pendingYouTubeRef.current.set(itemId, {
      itemId,
      videoId: result.id.videoId,
      title: decodedTitle,
      channelTitle: result.snippet.channelTitle,
      youtubeUrl: `https://www.youtube.com/watch?v=${result.id.videoId}`,
    });
    setItems(prev => [...prev, newItem]);
    onPendingYouTubeSongs?.(Array.from(pendingYouTubeRef.current.values()));
    setShowSearch(false);
  };

  const addCustomItem = () => {
    if (!customTitle.trim()) return;
    const newItem: PlanItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: addingType,
      title: customTitle.trim(),
      notes: '',
    };
    setItems(prev => [...prev, newItem]);
    setCustomTitle('');
  };

  const removeItem = (itemId: string) => {
    setItems(prev => prev.filter(i => i.id !== itemId));
    if (pendingYouTubeRef.current.delete(itemId)) {
      onPendingYouTubeSongs?.(Array.from(pendingYouTubeRef.current.values()));
    }
  };

  const updateItemKey = (itemId: string, key: string) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, key } : i));
  };

  const updateItemSection = (itemId: string, section: string) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, section: section || undefined } : i));
  };

  const updateItemNotes = (itemId: string, notes: string) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, notes } : i));
  };

  const updateItemLyrics = (itemId: string, lyrics: string) => {
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, lyrics } : i));
  };

  // --- Section management ---

  const addSection = (name: string) => {
    if (customSections.includes(name)) return;
    setCustomSections(prev => [...prev, name]);
  };

  const removeSection = (name: string) => {
    setCustomSections(prev => prev.filter(s => s !== name));
    // Clear section from items that had it
    setItems(prev => prev.map(i => i.section === name ? { ...i, section: undefined } : i));
  };

  // --- Template management ---

  const handleLoadTemplate = (template: SetlistTemplate) => {
    onLoadTemplate(template);
    setItems(template.items || []);
    setShowTemplateMenu(false);
  };

  return (
    <div className="rounded-[18px] border border-line bg-card">
      {/* Header */}
      <div className="px-3.5 py-2.5 border-b border-line flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand setlist' : 'Collapse setlist'}
          data-testid="setlist-collapse-toggle"
          className="flex items-center gap-2 text-t1 hover:text-t2"
        >
          {collapsed ? <ChevronRight size={16} className="text-t4" aria-hidden="true" /> : <ChevronDown size={16} className="text-t4" aria-hidden="true" />}
          <Music size={15} className="text-acc" aria-hidden="true" />
          <h3 className="text-[14px] font-semibold">Setlist</h3>
          <span className="text-[12.5px] text-t4">({items.length} item{items.length !== 1 ? 's' : ''})</span>
        </button>
        <div className="flex items-center gap-1.5 ml-auto">
          <Button
            ref={templateBtnRef}
            variant="ghost"
            size="sm"
            className="!h-[30px] !px-2.5 !text-[12.5px]"
            aria-haspopup={features.serviceTemplates ? 'menu' : undefined}
            aria-expanded={features.serviceTemplates ? showTemplateMenu : undefined}
            onClick={() => (features.serviceTemplates ? setShowTemplateMenu(!showTemplateMenu) : setShowTemplateUpgrade(true))}
            data-testid="load-template-button"
            icon={features.serviceTemplates ? <FolderOpen size={13} aria-hidden="true" /> : <LockKeyhole size={13} aria-hidden="true" />}
          >
            <span className="hidden sm:inline">Load template</span>
            {!features.serviceTemplates && <span className="text-[10.5px] font-semibold px-1.5 py-px rounded-full bg-amb-bg text-amb">Basic</span>}
          </Button>
          <Menu open={showTemplateMenu} onClose={() => setShowTemplateMenu(false)} anchorRef={templateBtnRef} label="Templates" width={260} align="end">
            {templates.length === 0 ? (
              <p className="px-2.5 py-2 text-[13px] text-t4">No templates saved yet.</p>
            ) : (
              templates.map((t) => (
                <MenuItem key={t.id} onSelect={() => handleLoadTemplate(t)} hint={`${t.items?.length || 0} items`}>
                  <span className="truncate block">{t.name}</span>
                </MenuItem>
              ))
            )}
          </Menu>
          {items.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="!h-[30px] !px-2.5 !text-[12.5px]"
              onClick={() => (features.serviceTemplates ? setShowSaveTemplate(true) : setShowTemplateUpgrade(true))}
              data-testid="save-template-button"
              icon={features.serviceTemplates ? <Save size={13} aria-hidden="true" /> : <LockKeyhole size={13} aria-hidden="true" />}
            >
              <span className="hidden sm:inline">Save template</span>
              {!features.serviceTemplates && <span className="text-[10.5px] font-semibold px-1.5 py-px rounded-full bg-amb-bg text-amb">Basic</span>}
            </Button>
          )}
          {items.length > 0 && (
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                className="!h-[30px] !px-2.5 !text-[12.5px]"
                onClick={share.handleShare}
                disabled={share.isSharing}
                aria-label={share.isSharing ? 'Sharing…' : 'Share link'}
                icon={<Share2 size={13} aria-hidden="true" />}
              >
                <span className="hidden sm:inline">{share.isSharing ? 'Sharing…' : 'Share link'}</span>
              </Button>
              {share.shareTooltip && (
                <div role="status" className="wp-pop absolute right-0 top-full mt-1.5 px-2.5 py-1 text-[11.5px] font-semibold bg-ok-bg text-ok rounded-full whitespace-nowrap z-20">
                  Link copied!
                </div>
              )}
              {share.shareError && (
                <div role="alert" className="wp-pop absolute right-0 top-full mt-1.5 px-2.5 py-1 text-[11.5px] font-semibold bg-acc-bg text-acc rounded-full whitespace-nowrap z-20">
                  {share.shareError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {share.limitData && (
        <SetlistLimitModal
          limitData={share.limitData}
          deletingId={share.limitDeleting}
          onClose={() => share.setLimitData(null)}
          onReplace={share.handleLimitReplace}
          onDelete={share.handleLimitDelete}
        />
      )}

      <UpgradeFeatureModal
        isOpen={showTemplateUpgrade}
        onClose={() => setShowTemplateUpgrade(false)}
        featureName="Service Plan Templates"
        description="Save proven service plans and reuse them whenever your team needs a reliable starting point."
        requiredPlan="Basic"
      />

      {!collapsed && (
        <>
          <div className="p-3">
            {items.length === 0 ? (
              <div className="text-center py-7 px-4 rounded-[14px] bg-fill">
                <p className="text-[14px] font-semibold">No setlist items yet.</p>
                <p className="text-[12.5px] text-t4 mt-1">Add songs from the library or custom items below.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {items.map((item, idx) => (
                  <SetlistItem
                    key={item.id}
                    item={item}
                    idx={idx}
                    isYouTube={pendingYouTubeRef.current.has(item.id)}
                    onUpdateKey={updateItemKey}
                    onRemove={removeItem}
                    onUpdateSection={updateItemSection}
                    onUpdateNotes={updateItemNotes}
                    onUpdateLyrics={updateItemLyrics}
                    sections={customSections}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="px-3 pb-3 flex flex-col gap-3">
            {showSearch ? (
              <SongSearch churchId={churchId} groupId={groupId} onAddSong={addSongItem} onAddYouTube={addYouTubeItem} onClose={() => setShowSearch(false)} />
            ) : (
              <Button variant="dark" block className="!h-10" onClick={() => setShowSearch(true)} icon={<Plus size={15} aria-hidden="true" />}>
                Add song
              </Button>
            )}

            {/* Custom item: prayer, reading, announcement and so on. */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-full sm:w-40">
                <Select
                  size="sm"
                  aria-label="Item type"
                  value={addingType}
                  onChange={setAddingType}
                  options={ITEM_TYPES.filter((t) => t !== 'Song').map((t) => ({ value: t, label: t }))}
                />
              </div>
              <div className="flex-1 min-w-[160px]">
                <Input
                  height={36}
                  value={customTitle}
                  aria-label="Item title"
                  onChange={(e) => setCustomTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCustomItem()}
                  placeholder="Song title..."
                  className="!text-[13.5px]"
                />
              </div>
              {/* "Add" alone is ambiguous on this page, hence the testid. */}
              <Button variant="ghost" size="sm" className="!h-9" onClick={addCustomItem} disabled={!customTitle.trim()} data-testid="setlist-add-custom-item">
                Add
              </Button>
            </div>

            <SectionManager sections={customSections} onAddSection={addSection} onRemoveSection={removeSection} />
          </div>

          {showSaveTemplate && (
            <SaveTemplateModal
              onSave={(name) => {
                onSaveTemplate(name, items);
                setShowSaveTemplate(false);
              }}
              onCancel={() => setShowSaveTemplate(false)}
            />
          )}
        </>
      )}
    </div>
  );
};
